import {Component, EventEmitter, OnInit, signal} from '@angular/core';
import {Functions} from "../../../helpers/Functions";
import {ApiService} from "../../../services/api.service";
import {ActivatedRoute, Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {CommunityService} from "../../../services/community/community.service";
import {MetadataService} from "../../../services/metadata.service";
import {CommunityDTO} from "../../../DTO/CommunityDTO";
import {ReservationDTO} from "../../../DTO/PriorAppointment/ReservationDTO";
import Swal from "sweetalert2";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import { communityModuleIdFromRoute, priorAppointmentModuleBasePath } from '../prior-appointment-route.util';
import { firstValueFrom } from 'rxjs';
import { PaymentsService, PaymentResult } from '../../../services/payments/payments.service';
import { PurchaseResultDTO } from '../../../DTO/tickets/PurchaseResultDTO';
import { environment } from 'src/environments/environment';
import { loadPreReservationLabelsForModule, preAppointmentI18nKey } from '../../../services/prior-appointment/prior-appointment-labels.util';

@Component({
  selector: 'app-new-reservation',
  templateUrl: './new-reservation.component.html',
  styleUrl: './new-reservation.component.scss'
})
export class NewReservationComponent implements OnInit {
  isLoading = false;
  isPreparingForm = false;
  isSubmittingPayment = false;
  stepNumber: number;
  communityName = "";
  community: CommunityDTO;
  reservation: ReservationDTO;
  /** Instancia del módulo (ruta `prior-appointment/m/:moduleId`). */
  communityModuleId: number | null = null;
  readonly preReservationLabels = signal(false);
  public nextAttempt = new EventEmitter();

  constructor(private apiService: ApiService,
              private actRoute: ActivatedRoute,
              public activeModal: NgbActiveModal,
              private translate: TranslateService,
              private communityService: CommunityService,
              private metadataService: MetadataService,
              private paymentsService: PaymentsService,
              private router: Router) {

  }

  private instantPa(key: string, params?: object): string {
    const resolved = this.preReservationLabels() ? preAppointmentI18nKey(key) : key;
    return this.translate.instant(resolved, params);
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.reservation = new ReservationDTO();
    this.communityName = this.actRoute.snapshot.params['community'];
    this.communityModuleId = communityModuleIdFromRoute(this.actRoute);

    this.communityService.Get().subscribe(res => {
      this.community = res;
      void loadPreReservationLabelsForModule(
        this.apiService,
        this.community,
        this.communityModuleId,
        this.preReservationLabels,
        () => this.communityModuleId
      ).then(() => {
        if (this.community) {
          this.metadataService.updateMetadata({
            title: Functions.decode(this.community.name) + ' - ' + this.instantPa('PRIORAPPOINTMENT.title'),
          });
        }
      });
    });
    this.stepNumber = 1;
    this.isLoading = false;
  }

  attemptNext() {
    this.nextAttempt.emit(true);
  }

  nextStep() {
    if (this.stepNumber === 1) {
      void this.goToFormStepWithHold();
      return;
    }
    if (this.stepNumber == 2) {
      this.createReservation();
      return;
    }
    if (this.stepNumber < 3) {
      this.stepNumber++;
    }
  }

  async prevStep() {
    if (this.stepNumber === 2) {
      await this.releaseCurrentHoldAndKeepSelection();
      this.stepNumber = 1;
      return;
    }
    this.stepNumber--;
  }
  finishReservation(){
    const mid = this.communityModuleId;
    if (mid == null) {
      void this.router.navigate([this.communityName]);
      return;
    }
    void this.router.navigateByUrl('/' + priorAppointmentModuleBasePath(this.communityName, mid));
  }

  createReservation() {
    if (this.isSubmittingPayment || this.isPreparingForm) {
      return;
    }
    this.isSubmittingPayment = true;
    void this.executeCreateReservation().finally(() => {
      this.isSubmittingPayment = false;
    });
  }

  requiresPaymentFlow(): boolean {
    const amount = Number(this.reservation?.totalAmount ?? 0);
    return Number.isFinite(amount) && amount > 0;
  }

  private getRemainingReservedSeconds(): number | null {
    const untilIso = this.reservation?.reservedUntil;
    if (!untilIso) {
      return null;
    }
    const until = new Date(untilIso);
    if (Number.isNaN(until.getTime())) {
      return null;
    }
    const diff = Math.floor((until.getTime() - Date.now()) / 1000);
    return diff > 0 ? diff : 0;
  }

  private parseHoldResponse(hold: unknown): { id: number; reservedToken: string; reservedUntil: string } | null {
    if (hold == null || typeof hold !== 'object') {
      return null;
    }
    const h = hold as Record<string, unknown>;
    const idRaw = h['id'] ?? h['Id'];
    const id = typeof idRaw === 'number' ? idRaw : Number(idRaw);
    const token = h['reservedToken'] ?? h['ReservedToken'];
    const untilRaw = h['reservedUntil'] ?? h['ReservedUntil'];
    const reservedUntil =
      untilRaw instanceof Date
        ? untilRaw.toISOString()
        : typeof untilRaw === 'string'
          ? untilRaw
          : untilRaw != null
            ? String(untilRaw)
            : '';
    if (!Number.isFinite(id) || id <= 0 || typeof token !== 'string' || !token.trim() || !reservedUntil) {
      return null;
    }
    return { id, reservedToken: token, reservedUntil };
  }

  private isHoldValid(): boolean {
    const id = Number(this.reservation?.id);
    const token = this.reservation?.reservedToken;
    const untilIso = this.reservation?.reservedUntil;
    if (!id || !token || !untilIso) {
      return false;
    }
    const until = new Date(untilIso);
    if (Number.isNaN(until.getTime())) {
      return false;
    }
    return until.getTime() > Date.now();
  }

  private isMaxCapacityError(err: unknown): boolean {
    const e = err as { message?: unknown; error?: unknown; status?: unknown; code?: unknown } | null;
    const payload = e?.error as Record<string, unknown> | string | null | undefined;
    const codeParts: string[] = [];
    const messageParts: string[] = [];

    const push = (value: unknown): void => {
      if (typeof value === 'string' && value.trim()) {
        messageParts.push(value.trim().toLowerCase());
      }
    };
    const pushCode = (value: unknown): void => {
      if (typeof value === 'string' && value.trim()) {
        codeParts.push(value.trim().toUpperCase());
      }
    };

    pushCode(e?.code);
    push(e?.message);
    push(typeof payload === 'string' ? payload : undefined);
    if (payload && typeof payload === 'object') {
      pushCode(payload['code']);
      pushCode(payload['Code']);
      push(payload['message']);
      push(payload['Message']);
      push(payload['detail']);
      push(payload['Detail']);
      push(payload['title']);
      push(payload['Title']);
      push(payload['error']);
      push(payload['Error']);
    }

    const mergedCodes = codeParts.join(' | ');
    const merged = messageParts.join(' | ');
    return (
      mergedCodes.includes('MAX_CAPACITY') ||
      merged.includes('maxcapacity') ||
      merged.includes('no more places') ||
      merged.includes('plaza no disponible') ||
      merged.includes('plaça no disponible')
    );
  }

  private async ensureReservationHold(): Promise<boolean> {
    if (this.isHoldValid()) {
      return true;
    }
    try {
      const hold = await firstValueFrom(
        this.apiService.sendReservationHold(
          this.community.code,
          this.reservation,
          this.communityModuleId,
          this.accesCode,
        ),
      );
      const parsed = this.parseHoldResponse(hold);
      if (!parsed) {
        throw new Error('INVALID_HOLD');
      }
      this.reservation.id = parsed.id;
      this.reservation.status = hold?.status ?? this.reservation.status;
      this.reservation.reservedToken = parsed.reservedToken;
      this.reservation.reservedUntil = parsed.reservedUntil;
      this.reservation.basePrice = hold?.basePrice ?? this.reservation.basePrice;
      this.reservation.ivaAmount = hold?.ivaAmount ?? this.reservation.ivaAmount;
      this.reservation.feeAmount = hold?.feeAmount ?? this.reservation.feeAmount;
      this.reservation.extrasAmount = hold?.extrasAmount ?? this.reservation.extrasAmount;
      this.reservation.totalAmount = hold?.totalAmount ?? this.reservation.totalAmount;
      return true;
    } catch (err: any) {
      const isMaxCapacity = this.isMaxCapacityError(err);
      await Swal.fire({
        title: this.translate.instant('GENERIC.bad'),
        icon: 'error',
        html: isMaxCapacity
          ? this.translate.instant('ReservePlaces.NoMorePlacesAvailable')
          : this.instantPa('PRIORAPPOINTMENTCREATE.reservationSendError'),
        confirmButtonText: this.translate.instant('Accept'),
      });
      return false;
    }
  }

  private async goToFormStepWithHold(): Promise<void> {
    this.isPreparingForm = true;
    try {
      const ok = await this.ensureReservationHold();
      if (!ok) {
        return;
      }
      this.stepNumber = 2;
    } finally {
      this.isPreparingForm = false;
    }
  }

  private async releaseCurrentHoldAndKeepSelection(): Promise<void> {
    const reservationId = Number(this.reservation?.id ?? 0);
    const reservedToken = `${this.reservation?.reservedToken ?? ''}`.trim();
    if (reservationId > 0 && reservedToken) {
      await firstValueFrom(
        this.apiService.markReservationHoldAsError(
          this.community.code,
          reservationId,
          reservedToken,
          this.communityModuleId,
          this.accesCode,
        ),
      ).catch(() => undefined);
    }

    // Evita enviar un estado textual no necesario en el siguiente POST /Reservations/Hold.
    // El backend determina el estado del hold; aquí solo debemos limpiar datos de hold local.
    this.reservation.status = undefined;
    this.reservation.id = 0;
    this.reservation.reservedToken = undefined;
    this.reservation.reservedUntil = undefined;
  }

  private get accesCode(): string | null {
    return this.actRoute.snapshot.queryParamMap.get('accesCode');
  }

  onReservationExtrasChanged(): void {
    // Recalcula importes y renueva ventana de hold al cambiar extras del formulario.
    void firstValueFrom(
      this.apiService.sendReservationHold(
        this.community.code,
        this.reservation,
        this.communityModuleId,
        this.accesCode,
      ),
    ).then((hold) => {
      const parsed = this.parseHoldResponse(hold);
      if (parsed) {
        this.reservation.id = parsed.id;
        this.reservation.reservedToken = parsed.reservedToken;
        this.reservation.reservedUntil = parsed.reservedUntil;
      }
      this.reservation.status = hold?.status ?? this.reservation.status;
      this.reservation.basePrice = hold?.basePrice ?? this.reservation.basePrice;
      this.reservation.ivaAmount = hold?.ivaAmount ?? this.reservation.ivaAmount;
      this.reservation.feeAmount = hold?.feeAmount ?? this.reservation.feeAmount;
      this.reservation.extrasAmount = hold?.extrasAmount ?? this.reservation.extrasAmount;
      this.reservation.totalAmount = hold?.totalAmount ?? this.reservation.totalAmount;
    }).catch(() => undefined);
  }

  /** Misma regla que API RefreshReservationCodeIdentifier (resumen + PDF en paso 3). */
  private syncReservationCodeIdentifierFromForm(): void {
    const id = Number(this.reservation?.id ?? 0);
    if (!id) {
      return;
    }
    const name = `${this.reservation?.name ?? ''}`.trim();
    const surname = `${this.reservation?.surname ?? ''}`.trim();
    const codeName = name ? name[0] : 'X';
    const safeSurname = surname || 'XX';
    const codeSurname = safeSurname.substring(0, Math.min(2, safeSurname.length));
    this.reservation.codeIdentifier = `${id}-${codeName}${codeSurname}`;
  }

  private applyReservationCodeFromPurchaseResult(paymentStart: PurchaseResultDTO): void {
    const asAny = paymentStart as unknown as Record<string, unknown>;
    const reservationCode =
      paymentStart?.codeIdentifier ??
      (asAny['codeIdentifier'] as string | undefined) ??
      (asAny['CodeIdentifier'] as string | undefined) ??
      (asAny['reservationCode'] as string | undefined);
    if (reservationCode?.trim()) {
      this.reservation.codeIdentifier = reservationCode.trim();
      return;
    }
    this.syncReservationCodeIdentifierFromForm();
  }

  private async showReservationCreatedSuccess(): Promise<void> {
    await Swal.fire({
      title: this.translate.instant('GENERIC.good'),
      icon: 'success',
      text: this.instantPa('PRIORAPPOINTMENTCREATE.reservationCreatedSuccess'),
      confirmButtonText: this.instantPa('PRIORAPPOINTMENT.genericAccept'),
    });
    this.stepNumber++;
    this.activeModal.dismiss('reload');
  }

  /** @returns true if the caller should stop (error already shown). */
  private async handleReservationPurchaseStartErrors(paymentStart: PurchaseResultDTO): Promise<boolean> {
    const code = paymentStart?.error?.code;
    if (code === 'RESERVATION_EXPIRED' || code === 'INVALID_RESERVED_TOKEN') {
      await Swal.fire({
        title: this.translate.instant('Oops'),
        icon: 'warning',
        text: this.instantPa('PRIORAPPOINTMENTCREATE.reservationExpired'),
        confirmButtonText: this.translate.instant('Accept'),
      });
      this.reservation.id = 0;
      this.reservation.reservedToken = undefined;
      this.reservation.reservedUntil = undefined;
      this.stepNumber = 1;
      return true;
    }
    if (code === 'MAX_CAPACITY') {
      await Swal.fire({
        title: this.translate.instant('GENERIC.bad'),
        icon: 'error',
        html: this.translate.instant('ReservePlaces.NoMorePlacesAvailable'),
        confirmButtonText: this.translate.instant('Accept'),
      });
      return true;
    }
    return false;
  }

  private async executeCreateReservation(): Promise<void> {
    try {
      if (!this.isHoldValid()) {
        await Swal.fire({
          title: this.translate.instant('Oops'),
          icon: 'warning',
          text: this.instantPa('PRIORAPPOINTMENTCREATE.reservationExpired'),
          confirmButtonText: this.translate.instant('Accept'),
        });
        this.reservation.id = 0;
        this.reservation.reservedToken = undefined;
        this.reservation.reservedUntil = undefined;
        this.stepNumber = 1;
        return;
      }

      let byPass = false;
      if (this.requiresPaymentFlow() && !environment.production) {
        const result = await this.paymentsService.presentByPassPaymentForDemoActionSheet();
        if (result?.role === 'cancel') {
          return;
        }
        byPass = !!result?.data?.byPass;
      }

      const paymentStart: PurchaseResultDTO = await firstValueFrom(
        this.apiService.sendReservationWithPayment(
          this.community.code,
          this.reservation,
          byPass,
          this.communityModuleId,
          this.accesCode,
        ),
      );
      if (await this.handleReservationPurchaseStartErrors(paymentStart)) {
        return;
      }

      const paymentRes = await this.paymentsService.processPurchaseResult(
        paymentStart,
        null as any,
        this.getRemainingReservedSeconds(),
      );

      if (paymentRes === PaymentResult.SUCCESS) {
        this.applyReservationCodeFromPurchaseResult(paymentStart);
        await this.showReservationCreatedSuccess();
        return;
      }

      if (paymentRes === PaymentResult.FAILED) {
        if (paymentStart?.purchaseUuid) {
          void firstValueFrom(
            this.apiService.markReservationPaymentError(
              this.community.code,
              paymentStart.purchaseUuid,
              this.communityModuleId,
              this.accesCode,
            ),
          ).catch(() => undefined);
        }
        Swal.fire({
          title: this.translate.instant('GENERIC.bad'),
          icon: 'error',
          html: this.translate.instant('PAYMENT.PaymentFailure'),
          confirmButtonText: this.translate.instant('Accept'),
        });
        return;
      }

      Swal.fire({
        title: this.translate.instant('Oops'),
        icon: 'warning',
        text: this.instantPa('PRIORAPPOINTMENTCREATE.paymentCancelled'),
        confirmButtonText: this.translate.instant('Accept'),
      });
    } catch (err) {
      const isMaxCapacity = this.isMaxCapacityError(err);
      Swal.fire({
        title: this.translate.instant('GENERIC.bad'),
        icon: 'error',
        html: isMaxCapacity
          ? this.translate.instant('ReservePlaces.NoMorePlacesAvailable')
          : this.instantPa('PRIORAPPOINTMENTCREATE.reservationSendError'),
        confirmButtonText: this.translate.instant('Accept'),
      });
    }
  }
}
